// js/common/navigation/tabs.js
import { animate as animate8 } from 'vendor';
if (!window.customElements.get('x-tabs')) {
	var Tabs = class extends HTMLElement {
		static get observedAttributes() {
			return ['selected-index'];
		}
		constructor() {
			super();
			if (!this.shadowRoot) {
				this.attachShadow({ mode: 'open' }).appendChild(this.querySelector('template').content.cloneNode(true));
			}
			if (Shopify.designMode) {
				this.addEventListener('shopify:block:select', (event) => (this.selectedIndex = this.buttons.indexOf(event.target)));
			}
			this.addEventListener('keydown', this._handleKeyboard);
		}
		connectedCallback() {
			this._abortController = new AbortController();
			this.buttons = Array.from(
				this.shadowRoot.querySelector('slot[name="title"]').assignedNodes(),
				(item) => (item.matches('button') && item) || item.querySelector('button')
			);
			this.panels = Array.from(this.shadowRoot.querySelector('slot[name="content"]').assignedNodes());
			this.buttons.forEach((button, index) =>
				button.addEventListener('click', () => (this.selectedIndex = index), { signal: this._abortController.signal })
			);
			this.selectedIndex = this.selectedIndex;
			this._setupAccessibility();
		}
		disconnectedCallback() {
			this._abortController.abort();
		}
		get animationDuration() {
			return this.hasAttribute('animation-duration') ? parseFloat(this.getAttribute('animation-duration')) : 0.15;
		}
		get selectedIndex() {
			return parseInt(this.getAttribute('selected-index')) || 0;
		}
		set selectedIndex(index) {
			this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
			this.style.setProperty('--selected-index', this.selectedIndex.toString());
			this.style.setProperty('--item-count', this.buttons.length.toString());
		}
		attributeChangedCallback(name, oldValue, newValue) {
			this.buttons.forEach((button, index) => button.setAttribute('aria-selected', index === parseInt(newValue) ? 'true' : 'false'));
			if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
				this._transition(this.panels[parseInt(oldValue)], this.panels[parseInt(newValue)]);
			}
		}
		_setupAccessibility() {
			const componentID = crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4);
			this.buttons.forEach((button, index) => {
				button.setAttribute('role', 'tab');
				button.setAttribute('aria-controls', `tab-panel-${componentID}-${index}`);
				button.id = `tab-${componentID}-${index}`;
			});
			this.panels.forEach((panel, index) => {
				panel.setAttribute('role', 'tabpanel');
				panel.setAttribute('aria-labelledby', `tab-${componentID}-${index}`);
				panel.id = `tab-panel-${componentID}-${index}`;
				panel.hidden = index !== this.selectedIndex;
			});
		}
		/**
		 * As per https://www.w3.org/WAI/ARIA/apg/example-index/tabs/tabs-automatic.html, when a tab is currently focused,
		 * left and right arrow should switch the tab
		 */
		_handleKeyboard(event) {
			const index = this.buttons.indexOf(document.activeElement);
			if (index === -1 || !['ArrowLeft', 'ArrowRight'].includes(event.key)) {
				return;
			}
			if (event.key === 'ArrowLeft') {
				this.selectedIndex = (this.selectedIndex - 1 + this.buttons.length) % this.buttons.length;
			} else {
				this.selectedIndex = (this.selectedIndex + 1 + this.buttons.length) % this.buttons.length;
			}
			this.buttons[this.selectedIndex].focus();
		}
		/**
		 * Perform a custom transition (can be overridden in subclasses). To "from" and "to" are hash representing the panel
		 */
		async _transition(fromPanel, toPanel) {
			await animate8(fromPanel, { opacity: [1, 0] }, { duration: this.animationDuration }).finished;
			fromPanel.hidden = true;
			toPanel.hidden = false;
			await animate8(toPanel, { opacity: [0, 1] }, { duration: this.animationDuration }).finished;
		}
	};
	window.customElements.define('x-tabs', Tabs);
}
